import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useServices } from "@/app/use-services";
import { errorMessage } from "@/lib/api/errors";
import { useInvalidateProfile } from "@/features/profile/use-profile";
import { Button } from "@/ui/Button";
import { Screen } from "@/ui/Screen";
import { Body, Display } from "@/ui/Text";
import { Working } from "@/ui/Working";
import { toBirthData } from "../draft";
import { PATHS } from "../destinations";

/** Only paths inside the app are honoured as a landing; anything else is Today. */
function safeThen(raw: string | null): string {
  return raw && raw.startsWith("/") && !raw.startsWith("//") ? raw : PATHS.today;
}

/**
 * Posts the draft as the account's birth profile, then hands over to the app.
 *
 * Framed unlike the other working screens: this is the only one whose work
 * can fail, and an unframed failure is indistinguishable from a save that is
 * merely slow. Consent, remembered in the draft, is recorded here too — it is
 * for our records, so it is not awaited.
 */
export function SavingRoute() {
  const { api, draft: store, analytics } = useServices();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const invalidateProfile = useInvalidateProfile();
  const [error, setError] = useState<string | null>(null);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const draft = store.getSnapshot();
    const birth = toBirthData(draft);
    if (!birth) {
      navigate(PATHS.root, { replace: true });
      return;
    }
    void (async () => {
      try {
        await api.createBirthProfile(birth);
        // A new account with its chart saved: the sign-up worth reporting.
        analytics.track({ name: "signed up" });
        if (draft.consented) void api.recordAiConsent().catch(() => undefined);
        await invalidateProfile();
        store.clear();
        navigate(safeThen(params.get("then")), { replace: true });
      } catch (caught) {
        analytics.track({ name: "chart save failed" });
        setError(errorMessage(caught) || "We couldn’t save your chart.");
      }
    })();
  }, [api, store, analytics, navigate, params, invalidateProfile]);

  if (!error) return <Working message="Saving your chart." />;
  return (
    <Screen centred foot={<Button label="Try again" onClick={() => { started.current = false; setError(null); }} />}>
      <Display>We couldn’t save your chart.</Display>
      <Body className="mt-3 text-mid">{error}</Body>
    </Screen>
  );
}
